import { useRef, useState, useEffect } from "react";
import {
  Grid,
  Flex,
  Text,
  InputGroup,
  FormControl,
  FormLabel,
  InputLeftElement,
  Box,
  Input,
  VStack,
  Button,
  useToast,
  Spinner,
  Heading,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import uploadFile from "../../assets/upload-file.svg";
import { DeleteIcon } from "@chakra-ui/icons";
import ItemBox from "../ItemBox/ItemBox";
import { RESUME_PARSER_URL, TARM_AWS_URL } from "../../api/api";
import ItemBoxSkeleton from "../ItemBox/ItemBoxSkeleton";
import MatchResumeGrid from "./MatchResumeGrid";
import { getRunID } from "../../util/runID";
import { useOutletContext } from "react-router-dom";
import axios from "axios";

const MatchResume = () => {
  const [setHasStripe, setProgressValue] = useOutletContext();
  const toast = useToast();
  const inputRef = useRef();
  const [source] = useState(axios.CancelToken.source());
  const careerStages = [
    "manager",
    "lead",
    "senior associate",
    "senior",
    "associate",
    "junior",
  ];
  const [uploadResume, setUploadResume] = useState("");
  const [selectedJobProfiles, setSelectedJobProfiles] = useState([]);
  const [allJobProfiles, setAllJobProfiles] = useState([]);
  const [careerStage, setCareerStage] = useState(null);
  const [topJobProfiles, setTopJobProfiles] = useState([]);
  const [topResumePools, setTopResumePools] = useState([]);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [serializedRowData, setSerializedRowData] = useState([]);

  const handleDelete = () => {
    setUploadResume("");
    inputRef.current.value = "";
    setSerializedRowData([]);
  };

  const handleJobProfileChange = (selectedItems) => {
    if (selectedItems.length > 5) return;
    setSelectedJobProfiles(selectedItems);
  };

  useEffect(() => {
    fetchPoolsAndJobProfiles();
    return () => {
      if (source) {
        source.cancel();
      }
    };
  }, []);

  const fetchPoolsAndJobProfiles = async () => {
    try {
      const response = await TARM_AWS_URL.get("/jd-parser/get-pools-jds-count", {
        cancelToken: source.token,
      });
      setTopJobProfiles(response.data.jd);
      setTopResumePools(response.data.pool);
      setAllJobProfiles(response.data.jd);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      toast({
        title: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const uploadResumeHandler = async () => {
    setIsBtnLoading(true);
    setHasStripe(true);
    setProgressValue(10);
    const formData = new FormData();
    formData.append("file", uploadResume);
    try {
      const response = await RESUME_PARSER_URL.post("uploadresume", formData, {
        cancelToken: source.token,
      });
      if (response.status === 200) {
        toast({
          title: "Resume uploaded successfully. Now Parsing resume.",
          status: "success",
          duration: 5000,
          isClosable: true,
          variant: "left-accent",
          position: "bottom",
        });
        setProgressValue(30);
        parseResumeHandler();
      }
    } catch (error) {
      console.log(`Error: ${error.message}`);
      toast({
        title: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsBtnLoading(false);
      setProgressValue(0);
      setHasStripe(false);
    }
  };

  const parseResumeHandler = async () => {
    const formData = new FormData();
    const run_id = getRunID();
    formData.append("run_id", run_id);
    try {
      const response = await RESUME_PARSER_URL.post("parseresume", formData, {
        cancelToken: source.token,
      });
      if (response.status === 200) {
        toast({
          title:
            "Resume successfully parsed. Getting ranking against the job profiles.",
          status: "success",
          duration: 5000,
          isClosable: true,
          variant: "left-accent",
          position: "bottom",
        });
        setProgressValue(60);
        rankingHandler(run_id);
      }
    } catch (error) {
      console.log(`Error: ${error.message}`);
      toast({
        title: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsBtnLoading(false);
      setProgressValue(0);
      setHasStripe(false);
    }
  };

  const rankingHandler = async (run_id_resume) => {
    setProgressValue(80);
    try {
      const postObject = {};
      postObject["run_id_resume"] = run_id_resume;
      postObject["run_id_jds"] = selectedJobProfiles.map((jp) => ({
        jd: jp.value,
        run_id: jp.run_id,
      }));
      postObject["career_stage"] = careerStage.value;
      console.log(postObject);
      const response = await RESUME_PARSER_URL.post("ranking", postObject, {
        cancelToken: source.token,
      });
      console.log(response);
      let serializedRowData = [];
      response.data.forEach((element) => {
        const item = JSON.parse(element);
        let obj = {};
        obj["profile"] = item.jd[0];
        obj["email_id"] = item.email_id[0];
        obj["keyword_present"] = item.keyword_present[0];
        obj["matched_skills"] = item.matched_skills[0];
        obj["all_resume_skills"] = item.all_resume_skills[0];
        obj["job_title"] = item.job_title[0];
        obj["req_id"] = item.req_id[0];
        obj["score_combined"] = item.score_combined[0];
        serializedRowData.push(obj);
      });
      setProgressValue(98);
      setSerializedRowData(serializedRowData);
      setProgressValue(0);
      setHasStripe(false);
    } catch (error) {
      console.log(`Error: ${error.message}`);
      toast({
        title: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsBtnLoading(false);
      setProgressValue(0);
      setHasStripe(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!(selectedJobProfiles.length && uploadResume)) return;
    uploadResumeHandler();
  };

  return (
    <VStack>
      <Heading>Match a resume with existing jobs</Heading>
      <br />
      {isLoading && <ItemBoxSkeleton />}

      {!isLoading && (
        <>
          <Grid templateColumns="repeat(3, 1fr)" gap={3}>
            {topJobProfiles.slice(0, 6).map((jp) => (
              <ItemBox label={jp.PK} value={jp.resumes} key={jp.PK} />
            ))}
          </Grid>
          <Grid templateColumns="repeat(3, 1fr)" gap={3}>
            {topResumePools.slice(0, 6).map((pool) => (
              <ItemBox label={pool.PK} value={pool.resumes} key={pool.PK} />
            ))}
          </Grid>
        </>
      )}

      <form onSubmit={handleSubmit}>
        <Grid templateColumns="repeat(2, 1fr)" gap={10} pt={10}>
          <Box width={350}>
            <FormControl isRequired>
              <FormLabel htmlFor="jobProfiles">
                Please select job profile upto 5
              </FormLabel>
              <Select
                isMulti
                id="jobProfiles"
                name="jobProfiles"
                options={allJobProfiles.map((jp) => ({
                  label: jp.PK,
                  value: jp.PK,
                  run_id: jp.run_id,
                }))}
                value={selectedJobProfiles}
                onChange={handleJobProfileChange}
                placeholder="Search"
                closeMenuOnSelect={false}
              />
            </FormControl>
          </Box>

          <Box width={350}>
            <FormControl isRequired>
              <FormLabel htmlFor="uploadResume">
                Please upload resume here
              </FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <img
                    style={{ height: "22px" }}
                    src={uploadFile}
                    alt="Upload File"
                  />
                </InputLeftElement>
                <input
                  type="file"
                  onChange={(e) => setUploadResume(e.target.files[0])}
                  accept=".pdf"
                  name={uploadResume.name}
                  ref={inputRef}
                  style={{ display: "none" }}
                />
                <Input
                  id="uploadResume"
                  placeholder="Upload resume"
                  onClick={() => inputRef.current.click()}
                  className="cursor-pointer "
                  readOnly={true}
                  value={(uploadResume && uploadResume.name) || ""}
                />
              </InputGroup>
            </FormControl>
            {uploadResume && (
              <Box p="2">
                <Flex justify="space-between">
                  <Text>{uploadResume.name}</Text>
                  <DeleteIcon
                    mt={1}
                    color="gray"
                    className="cursor-pointer close-icon"
                    onClick={handleDelete}
                  />
                </Flex>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid templateColumns="repeat(2, 1fr)" gap={10} pt={5}>
          <Box width={350}>
            <FormControl isRequired>
              <FormLabel htmlFor="careerStages">
                Please select career stage
              </FormLabel>
              <Select
                id="careerStages"
                name="careerStages"
                options={careerStages.map((stage) => ({
                  label: stage,
                  value: stage,
                }))}
                value={careerStage}
                onChange={(e) => setCareerStage(e)}
                placeholder="Search"
                closeMenuOnSelect={true}
              />
            </FormControl>
          </Box>
          <Box display="flex" alignItems="end" justifyContent="start">
            <Button
              type="submit"
              colorScheme="blue"
              disabled={
                !(selectedJobProfiles.length && uploadResume && careerStage) ||
                isBtnLoading
              }
            >
              {isBtnLoading && <Spinner size="sm" mr={2} />}
              Next
            </Button>
          </Box>
        </Grid>
      </form>
      <br />

      {serializedRowData.length > 0 && (
        <>
          {/* <Box borderWidth="1px" borderRadius="lg" width="650px" p="4">
            <Flex justify="space-between">
              <Box>View Results for</Box>
              <ButtonGroup gap="1">
                <Button size="sm">Data Analyst</Button>
                <Button size="sm">DevOps</Button>
                <Button size="sm">Big Data</Button>
              </ButtonGroup>
            </Flex>
          </Box>
          <br /> */}
          <MatchResumeGrid serializedRowData={serializedRowData} />
        </>
      )}
    </VStack>
  );
};

export default MatchResume;
